import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, Music2, Clock } from "lucide-react";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";

const footerLinks = {
  services: [
    { name: "Photography", href: "/photography-by-pixie-clicks" },
    { name: "Cinematography", href: "/cinematography" },
    { name: "Photo Booth", href: "/photobooth" },
    { name: "Combo Packages", href: "/combo-packages" },
  ],
  company: [
    { name: "About Us", href: "/about-pixie-clicks" },
    { name: "Our Story", href: "/our-story-philosophy" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "FAQ", href: "/faq" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
  ],
};

export function Footer() {
  const { getSetting } = useBusinessSettings();

  const email = getSetting("email");
  const phone = getSetting("phone");
  const phoneDisplay = getSetting("phone_display");
  const serviceAreas = getSetting("service_areas");
  const businessHours = getSetting("business_hours");
  const instagramUrl = getSetting("instagram_url");
  const facebookUrl = getSetting("facebook_url");
  const youtubeUrl = getSetting("youtube_url");
  const tiktokUrl = getSetting("tiktok_url");

  const socialLinks = [
    { name: "Instagram", icon: Instagram, href: instagramUrl },
    { name: "Facebook", icon: Facebook, href: facebookUrl },
    { name: "YouTube", icon: Youtube, href: youtubeUrl },
    ...(tiktokUrl ? [{ name: "TikTok", icon: Music2, href: tiktokUrl }] : []),
  ].filter((link) => link.href);

  return (
    <footer className="bg-foreground text-primary-foreground">
      {/* Mobile/Tablet - Simplified centered layout */}
      <div className="lg:hidden flex flex-col items-center text-center py-8 space-y-6">
        <Link to="/" className="inline-block">
          <span className="font-serif text-2xl font-semibold tracking-tight">
            Pixie<span className="text-primary">Clicks</span>
          </span>
        </Link>
        
        <div className="flex gap-4">
          {socialLinks.map((social) => (
            <a
              key={social.name}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-primary-foreground/10 p-2.5 transition-colors hover:bg-primary hover:text-primary-foreground"
              aria-label={social.name}
            >
              <social.icon className="h-5 w-5" />
            </a>
          ))}
        </div>
        
        <p className="text-sm text-primary-foreground/50">
          © {new Date().getFullYear()} Pixie Clicks. All rights reserved.
        </p>
      </div>

      {/* Desktop - Full layout */}
      <div className="hidden lg:block container-wide section-padding">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block">
              <span className="font-serif text-2xl font-semibold tracking-tight">
                Pixie<span className="text-primary">Clicks</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-primary-foreground/70 max-w-xs">
              Beautiful wedding photography without the luxury price tag. Serving {serviceAreas}.
            </p>
            <div className="mt-6 flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-primary-foreground/10 p-2.5 transition-colors hover:bg-primary hover:text-primary-foreground"
                  aria-label={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-serif text-lg font-semibold">Services</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 transition-colors hover:text-primary"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-serif text-lg font-semibold">Company</h3>
            <ul className="mt-4 space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/70 transition-colors hover:text-primary"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-lg font-semibold">Contact</h3>
            <ul className="mt-4 space-y-3">
              <li className="flex items-center gap-3 text-sm text-primary-foreground/70">
                <Mail className="h-4 w-4 text-primary" />
                <a href={`mailto:${email}`} className="hover:text-primary transition-colors">
                  {email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-primary-foreground/70">
                <Phone className="h-4 w-4 text-primary" />
                <a href={`tel:${phone}`} className="hover:text-primary transition-colors">
                  {phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-primary-foreground/70">
                <MapPin className="h-4 w-4 text-primary mt-0.5" />
                <span>{serviceAreas}</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-primary-foreground/70">
                <Clock className="h-4 w-4 text-primary mt-0.5" />
                <span>{businessHours}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-primary-foreground/10 pt-8 md:flex-row">
          <p className="text-sm text-primary-foreground/50">
            © {new Date().getFullYear()} Pixie Clicks. All rights reserved.
          </p>
          <div className="flex gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-sm text-primary-foreground/50 transition-colors hover:text-primary"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
